import { useState, useMemo } from 'react';

const fmt = (n: number) => `${Math.round(n).toLocaleString('pl-PL')} zł`;

function SliderField({
  label,
  value,
  display,
  min,
  max,
  step,
  marks,
  onChange,
}: {
  label: string;
  value: number;
  display: React.ReactNode;
  min: number;
  max: number;
  step: number;
  marks: [string, string, string];
  onChange: (v: number) => void;
}) {
  return (
    <div className="mb-5">
      <div className="flex justify-between items-baseline mb-2">
        <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider">{label}</span>
        <span className="text-sm font-extrabold text-foreground-950 tracking-tight">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
      />
      <div className="flex justify-between text-[10px] font-semibold text-foreground-400 mt-1">
        <span>{marks[0]}</span>
        <span>{marks[1]}</span>
        <span>{marks[2]}</span>
      </div>
    </div>
  );
}

export default function CreditworthinessCalculator() {
  const [income, setIncome] = useState(8000);
  const [persons, setPersons] = useState(2);
  const [debt, setDebt] = useState(0);
  const [limits, setLimits] = useState(0);
  const [years, setYears] = useState(30);
  const [rate, setRate] = useState(5.6);

  const n = years * 12;

  const { capacity, lo, hi, maxRata, living, limCharge, dstiCap, bufRate, disposable } = useMemo(() => {
    const living = 1500 + (persons - 1) * 1000;
    const limCharge = limits * 0.03;
    const dsti = income > 10000 ? 0.6 : 0.5;
    const disposable = income - living - debt - limCharge;
    const dstiCap = income * dsti - debt - limCharge;
    const maxRata = Math.max(0, Math.min(disposable, dstiCap));

    const bufRate = rate + 2.5;
    const m = bufRate / 1200;
    const capacity = maxRata > 0 ? (maxRata * (1 - Math.pow(1 + m, -n))) / m : 0;
    const lo = capacity * 0.85;
    const hi = capacity * 1.15;

    return { capacity, lo, hi, maxRata, living, limCharge, dstiCap, bufRate, disposable };
  }, [income, persons, debt, limits, years, rate, n]);

  const hintText = useMemo(() => {
    if (capacity <= 0) {
      return (
        <>
          Przy tych parametrach zdolność wychodzi ujemna — ale to nie przekreśla kredytu. Ekspert kredytowy zna banki liczące koszty utrzymania i zobowiązania łagodniej. <b>Zadzwoń: 698 084 044.</b>
        </>
      );
    }
    if (debt + limCharge > income * 0.2) {
      return (
        <>
          Zobowiązania mocno obniżają Twoją zdolność. Konsolidacja lub zamknięcie limitów przed wnioskiem może ją podnieść nawet o kilkadziesiąt procent — ekspert kredytowy podpowie, co zamknąć najpierw.
        </>
      );
    }
    return (
      <>
        To szacunek uśredniony — w najlepszym dla Ciebie banku zdolność może być o <b>15–20% wyższa</b>. Ekspert kredytowy sprawdzi to bezpłatnie w kilkunastu bankach.
      </>
    );
  }, [capacity, debt, limCharge, income]);

  const personOptions = [1, 2, 3, 4];

  return (
    <div>
      {/* Hero */}
      <section className="bg-foreground-950 text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary-400 mb-3 opacity-0 animate-slide-up animation-delay-100">
            Kalkulator · Zdolność kredytowa
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mx-auto max-w-2xl text-center leading-tight mb-4 opacity-0 animate-slide-up animation-delay-200">
            Ile kredytu hipotecznego możesz dostać?
          </h1>
          <p className="text-sm md:text-base text-white/60 mx-auto max-w-xl text-center leading-relaxed opacity-0 animate-slide-up animation-delay-300">
            Wpisz dochody i zobowiązania, a kalkulator oszacuje Twoją zdolność kredytową według zasad zbliżonych do bankowych — z buforem stóp procentowych wymaganym przez KNF.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 items-start">
            {/* Inputs */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Twoja sytuacja
              </h3>

              <SliderField
                label="Dochód netto gospodarstwa (mies.)"
                value={income}
                display={
                  <>
                    {income.toLocaleString('pl-PL')}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">zł</span>
                  </>
                }
                min={3000}
                max={40000}
                step={250}
                marks={['3 tys.', '20 tys.', '40 tys.']}
                onChange={setIncome}
              />

              <div className="mb-5">
                <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider block mb-2">
                  Liczba osób w gospodarstwie
                </span>
                <div className="grid grid-cols-4 border-[1.5px] border-background-200 rounded-xl overflow-hidden">
                  {personOptions.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPersons(p)}
                      className={`py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                        persons === p
                          ? 'bg-primary-500 text-white'
                          : 'bg-white text-foreground-500 hover:bg-background-100'
                      }`}
                    >
                      {p === 4 ? '4+' : p}
                    </button>
                  ))}
                </div>
              </div>

              <SliderField
                label="Raty innych kredytów (mies.)"
                value={debt}
                display={
                  <>
                    {debt.toLocaleString('pl-PL')}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">zł</span>
                  </>
                }
                min={0}
                max={10000}
                step={100}
                marks={['0', '5 tys.', '10 tys.']}
                onChange={setDebt}
              />

              <SliderField
                label="Limity kart i kont (łącznie)"
                value={limits}
                display={
                  <>
                    {limits.toLocaleString('pl-PL')}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">zł</span>
                  </>
                }
                min={0}
                max={100000}
                step={1000}
                marks={['0', '50 tys.', '100 tys.']}
                onChange={setLimits}
              />

              <SliderField
                label="Okres kredytu"
                value={years}
                display={
                  <>
                    {years}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">
                      {years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat'}
                    </span>
                  </>
                }
                min={5}
                max={35}
                step={1}
                marks={['5', '20', '35']}
                onChange={setYears}
              />

              <SliderField
                label="Oprocentowanie"
                value={rate}
                display={
                  <>
                    {rate.toFixed(2).replace('.', ',')}
                    <span className="text-foreground-400 font-semibold text-xs">%</span>
                  </>
                }
                min={4}
                max={10}
                step={0.05}
                marks={['4%', '7%', '10%']}
                onChange={setRate}
              />
            </div>

            {/* Results */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Szacunkowa zdolność kredytowa
              </h3>

              <div className="bg-foreground-950 rounded-xl text-white p-6 text-center">
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-primary-400">
                  Możesz pożyczyć około
                </div>
                <div className="text-2xl md:text-3xl font-extrabold tracking-tight mt-1">
                  {capacity > 0 ? fmt(capacity) : '0 zł'}
                </div>
                <div className="text-sm text-white/60 mt-1">
                  {capacity > 0 ? `w zależności od banku: ${fmt(lo)} – ${fmt(hi)}` : '—'}
                </div>
              </div>

              <div className="mt-4">
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Maksymalna bezpieczna rata</span>
                  <b className="text-foreground-900">{fmt(maxRata)}</b>
                </div>
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Szacunkowe koszty utrzymania</span>
                  <b className="text-foreground-900">{fmt(living)}</b>
                </div>
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Obciążenie zobowiązaniami</span>
                  <b className="text-foreground-900">{fmt(debt + limCharge)}</b>
                </div>
                <div className="flex justify-between py-2.5 text-sm">
                  <span className="text-foreground-500">Rata liczona z buforem KNF</span>
                  <b className="text-foreground-900">
                    {bufRate.toFixed(2).replace('.', ',')}% ({rate.toFixed(2).replace('.', ',')}% + 2,5 p.p.)
                  </b>
                </div>
              </div>

              <div className={`rounded-xl p-4 mt-5 text-sm leading-relaxed ${capacity <= 0 || debt + limCharge > income * 0.2 ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-primary-100 border border-primary-200 text-primary-800'}`}>
                {hintText}
              </div>

              <p className="text-[11px] text-foreground-400 leading-relaxed mt-4">
                Szacunek według uproszczonej metodologii: limit raty do 50–60% dochodu netto (DSTI), koszty utrzymania 1 500 zł + 1 000 zł za każdą kolejną osobę, 3% limitów kredytowych jako miesięczne obciążenie, bufor stopy procentowej +2,5 p.p. zgodnie z rekomendacją KNF. Każdy bank liczy zdolność inaczej — różnice sięgają 20–30%.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-primary-500/10 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3">
                Bank policzy Ci mniej albo więcej — sprawdźmy, który da najwięcej
              </h2>
              <p className="text-sm text-white/60 max-w-xl mx-auto mb-8 leading-relaxed">
                Różnica w zdolności między bankami to często 100–200 tys. zł. Ekspert kredytowy bezpłatnie policzy Twoją zdolność w kilkunastu bankach i wskaże najlepszą ścieżkę.
              </p>
              <a
                href="tel:698084044"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-white font-extrabold text-lg rounded-full hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-lg shadow-primary-500/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl" />
                698 084 044
              </a>
              <div className="flex flex-wrap gap-5 justify-center mt-6">
                {[
                  'Bezpłatna analiza zdolności',
                  'Porównanie kilkunastu banków',
                  'Bez zobowiązań',
                ].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-xs text-white/50">
                    <i className="ri-check-line text-green-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="pb-10 md:pb-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <p className="text-[11px] text-foreground-400 leading-relaxed text-center max-w-3xl mx-auto">
            Kalkulator ma charakter poglądowy i edukacyjny — nie stanowi oferty ani zapewnienia o udzieleniu kredytu. Ostateczna zdolność kredytowa zależy od indywidualnej oceny banku, formy zatrudnienia, historii kredytowej i innych czynników.
          </p>
        </div>
      </section>
    </div>
  );
}